import { Component, effect, inject, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { FolderNurseRecordsPageStore } from './folder-nurse-records-page.store';
import { FilterInput } from '@it2go/data-grid';
import { Router } from '@angular/router';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { WorkGridItemObject } from '@it2go/types';
import { TableComponent } from '../../../../shared/components/kit/table/table.component';
import { MessageService, PrimeTemplate } from '@openng/optimus-ui/api';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmBuilder } from '../../../../shared/service/confirm.builder';
import config from '../../../../../config/config';
import {
    filesColumn,
    performedProceduresColumn,
    timeFromColumn,
    timeToColumn,
    usersChipColumn,
} from '../../../../shared/components/kit/table/table.fields';
import { TableColumnType } from '../../../../shared/components/kit/table/table.types';
import { WorkTypeEnum } from '@libs/types/src/common/enum/work-type.enum';
import { globalStore } from '../../../../shared/shared.global-service';
import { selectCurrentService } from '../../../store/person/person.selectors';
import { workVisitStateColor, workVisitStateEnumOptions } from '@libs/types/src/common/enum/work-visit-state.enum';
import { workUri } from 'packages/client/src/app/utils/router.utils';
import { FolderNurseRecordsPageStore2 } from './folder-nurse-records-page.store2';
import { getWorkDistinctVouchers } from '../../../../../utils/work.utils';
import { workGridItemFrag } from './folder-nurse-records-page.query';
import { SwitchInputComponent } from '../../../../../components/kit/input/switch-input/switch-input.component';
import { TagComponent } from '../../../../../components/kit/tag/tag.component';
import {
    FolderFormDataWorkColumnComponent,
} from '../../../components/services/forms/folder-form-data-work-column/folder-form-data-work-column.component';
import { FolderGlobalStore } from '../../../../../store/medicalService/folder.global-store';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { WorkPrintService } from '../../../../../components/medical-service/work/work-print.service';
import { featureOn } from '../../../../../feature-flag';

@Component({
    selector: 'app-folder-nurse-records-page',
    templateUrl: './folder-nurse-records-page.component.html',
    styleUrls: ['./folder-nurse-records-page.component.scss'],
    providers: [FolderNurseRecordsPageStore, FolderNurseRecordsPageStore2],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SwitchInputComponent,
        TableComponent,
        PrimeTemplate,
        TagComponent,
        FolderFormDataWorkColumnComponent,
        ButtonComponent,
    ],
})
export class FolderNurseRecordsPageComponent extends SubscriptionComponent {

    protected readonly folderStore = inject(FolderGlobalStore);
    protected readonly store = inject(FolderNurseRecordsPageStore);
    protected readonly store2 = inject(FolderNurseRecordsPageStore2);
    private readonly fb = inject(FormBuilder);
    private readonly confirmBuilder = inject(ConfirmBuilder);
    private readonly messageService = inject(MessageService);
    private readonly translate = inject(TranslateService);
    private readonly router = inject(Router);
    protected readonly workPrint = inject(WorkPrintService);
    protected readonly extendedWorkExportsEnabled = featureOn.EXTENDED_WORK_EXPORTS;


    form = this.fb.group({
        showBilled: this.fb.control<boolean>(false),
    });

    @ViewChild('worksTable') worksTable!: TableComponent;

    protected extraFilters: FilterInput[][] = [
        // DO NOT JUST ALTER -> indices used in code
        [
            {
                value: ['0'],
                column: 'folderId',
                operator: 'IN',
            },
        ],
        [
            {
                value: [WorkTypeEnum.VISIT, WorkTypeEnum.INTERVENTION],
                column: 'workType',
                operator: 'IN',
            },
        ],
        [
            {
                column: 'hasNoBilledTransport',
                operator: 'EQ',
                value: ['1'],
            },
        ],
        [
            {
                column: 'hasNoBilledProcedure',
                operator: 'EQ',
                value: ['1'],
            },
        ],
    ];

    protected columns = [
        {
            name: 'date',
            type: TableColumnType.Date,
            dataKey: 'dateFrom',
        },
        { ...timeFromColumn, name: 'timeFrom', dataKey: 'dateFrom' },
        { ...timeToColumn, name: 'timeTo', dataKey: 'dateTo' },
        performedProceduresColumn,
        {
            name: 'workData.wdnr.note',
            dataKey: 'workData.note',
            type: TableColumnType.String50,
            filterable: true,
        },
        {
            name: 'workData.medication',
            type: TableColumnType.String50,
        },
        {
            name: 'workData.state',
            type: TableColumnType.String,
            filterable: {
                type: 'select',
                selectOptions: () => workVisitStateEnumOptions,
            },
            exportAs: (item: any) => this.translate.instant(`workData.common.enum.workVisitState.${item}`),
        },
        filesColumn,
        {
            name: 'folderFormData',
        },
        {
            name: 'vouchers',
            dataKey: null,
            data: (item: WorkGridItemObject) => {
                return getWorkDistinctVouchers(item);
            },
            type: TableColumnType.Vouchers,
            voucherOptions: { showDetail: true, maxWidth: 'initial' },
        },
        usersChipColumn,
        { ...usersChipColumn, name: 'workData.assistant' },
    ];

    constructor() {
        super();

        effect(() => {
            this.store2.globalGlobalStore.organizationStore.selectedId();
            this.worksTable?.reload();
        });

        this.subs.push(
            this.form.controls.showBilled.valueChanges.subscribe(
                (showBilled) => {
                    const tmp: any = JSON.parse(
                        JSON.stringify(this.extraFilters),
                    );
                    if (!showBilled) {
                        tmp[2] = [
                            {
                                column: 'hasNoBilledTransport',
                                operator: 'EQ',
                                value: ['1'],
                            },
                        ];
                        tmp[3] = [
                            {
                                column: 'hasNoBilledProcedure',
                                operator: 'EQ',
                                value: ['1'],
                            },
                        ];
                    } else {
                        tmp[2] = [];
                        tmp[3] = [];
                    }
                    this.extraFilters = tmp;
                },
            ),
            globalStore.select(selectCurrentService).subscribe((service) => {
                if (service) {
                    const tmp: any = JSON.parse(
                        JSON.stringify(this.extraFilters),
                    );
                    const expertiseCode = service.expertise?.code || '';
                    if (['902', '925', '926'].includes(expertiseCode)) {
                        tmp[1] = [
                            {
                                value: [WorkTypeEnum.VISIT],
                                column: 'workType',
                                operator: 'IN',
                            },
                        ];
                    }
                    if (expertiseCode === '913') {
                        tmp[1] = [
                            {
                                value: [WorkTypeEnum.INTERVENTION],
                                column: 'workType',
                                operator: 'IN',
                            },
                        ];
                    }
                    tmp[0][0].value = this.store.nurseRecordSharingIds()
                        ? [
                            this.store.routeParam.folderId.value,
                            ...this.store.nurseRecordSharingIds()!.map(String),
                        ]
                        : [this.store.routeParam.folderId.value];
                    this.extraFilters = tmp;
                }
            }),
        );
    }

    async detail(work: WorkGridItemObject) {
        if (!this.isWorkBilled(work)) {
            await this.router.navigateByUrl(workUri(work, work.serviceId));
        } else {
            this.messageService.add({
                key: 'global-toasts',
                detail: this.translate.instant(
                    'folder.nurseRecords.cannotEditWork',
                ),
                severity: 'error',
                closable: true,
                life: config.messageLifetime,
            });
        }
    }

    delete(work: any) {
        if (!this.isWorkBilled(work.item)) {
            this.confirmBuilder.confirm(
                'folder.nurseRecords.deleteWorkConfirmation.header',
                'folder.nurseRecords.deleteWorkConfirmation.label',
                () => {
                    this.store2.api.deleteWork({ id: work.item.id }).then(() => this.worksTable?.reload());
                },
                { name: 'nurseRecordsWorkDeleteConfirm' + work.item.id },
                'pi-trash',
                work.event.target,
            );
        } else {
            this.messageService.add({
                key: 'global-toasts',
                detail: this.translate.instant(
                    'folder.nurseRecords.cannotDeleteWork',
                ),
                severity: 'error',
                closable: true,
                life: config.messageLifetime,
            });
        }
    }

    private isWorkBilled(work: WorkGridItemObject) {
        return work && work.isBilled;
    }

    protected readonly workVisitStateColor = workVisitStateColor;
    protected readonly workGridItemFrag = workGridItemFrag;

    protected printRecords(): void {
        this.workPrint.openBulk({
            selection: this.worksTable?.selection(),
            supportedWorkTypes: [WorkTypeEnum.VISIT, WorkTypeEnum.INTERVENTION],
            dateFrom: null,
            dateTo: null,
            folderIds: [
                Number(this.store.routeParam.folderId.value),
                ...(this.store.nurseRecordSharingIds() || []),
            ],
        });
    }

    protected printRecord(item: WorkGridItemObject): void {
        this.workPrint.openRow(item);
    }
}
